import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  RepoSpecsListResponse,
  SourceRepoCreateRequest,
  SourceRepoDeleteResponse,
  SourceRepoListResponse,
  SourceRepoSummary,
  SourceRepoSyncResponse,
  SourceRepoUpdateRequest,
} from "@oracus/shared/api/repos";

import { apiClient } from "./client.ts";

const REPOS_KEY = ["repos"] as const;

const NON_TERMINAL_STATUSES = new Set([
  "pending",
  "cloning",
  "analyzing",
  "generating",
  "indexing",
]);

export function useRepos() {
  return useQuery({
    queryKey: REPOS_KEY,
    queryFn: () => apiClient.get("/api/repos", SourceRepoListResponse),
    // Poll while any repo is mid-ingest so the UI tracks status without manual refresh.
    refetchInterval: (query) => {
      const data = query.state.data as
        | { repos: { status: string }[] }
        | undefined;
      if (!data) return false;
      return data.repos.some((r) => NON_TERMINAL_STATUSES.has(r.status))
        ? 3000
        : false;
    },
  });
}

export function useAddRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceRepoCreateRequest) =>
      apiClient.post("/api/repos", body, SourceRepoSummary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REPOS_KEY });
    },
  });
}

export function useSyncRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/api/repos/${id}/sync`, undefined, SourceRepoSyncResponse),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REPOS_KEY });
    },
  });
}

export function useUpdateRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: SourceRepoUpdateRequest;
    }) => apiClient.patch(`/api/repos/${id}`, body, SourceRepoSummary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REPOS_KEY });
    },
  });
}

export function useRepoSpecs(repoId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["repos", repoId, "specs"],
    queryFn: () =>
      apiClient.get(`/api/repos/${repoId}/specs`, RepoSpecsListResponse),
    enabled,
  });
}

export function useDeleteRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/api/repos/${id}`, SourceRepoDeleteResponse),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REPOS_KEY });
    },
  });
}
